// Market Context dashboard: a read-only board of the trend regime for major
// asset classes. Two views — Seasonal (1 Year average) and Generational (4 Year
// average) — each a ranked list of Ratio-Flow summary rows. Filterable by asset
// class (tabs) and sortable by ranking or asset type. No inputs, no auth.

import { useEffect, useState } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { loadAll, loading, loadedAt, results } from "../state/store";
import type { AssetCategory } from "../api/data";
import { gotoAuth } from "../api/auth";
import { refreshSession, session, signOut } from "../state/session";
import { isError, strengthDots, type AssetContext, type TrendRead } from "../engine/context";
import { formatAsOf, formatClock, formatLevel, signedPct, signedSharpe } from "./format";
import { Sparkline } from "./Sparkline";

const FEEDBACK_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSf42fQOzXH49bW1OutDzRHeIHOaNAMKYHbTYRy__Te8uDb-yA/viewform?usp=publish-editor";

/** Row of YouTube / X / LinkedIn / feedback icon links — identical to the
 *  backtest + portfolio apps' topbar. */
function SocialLinks() {
  return (
    <div class="social-links">
      <a href="https://www.youtube.com/marketheist" target="_blank" rel="noopener" class="social-link" title="YouTube">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
      </a>
      <a href="https://x.com/marketheist" target="_blank" rel="noopener" class="social-link" title="X (Twitter)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      </a>
      <a href="https://www.linkedin.com/company/marketheist/" target="_blank" rel="noopener" class="social-link" title="LinkedIn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
      </a>
      <a href={FEEDBACK_FORM_URL} target="_blank" rel="noopener" class="social-link" title="Send feedback">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
      </a>
    </div>
  );
}

/** Signed-out: Log In / Start Free (→ shared /signin). Signed-in: email chip
 *  with a Sign out dropdown. Same behaviour as the other apps via the shared,
 *  origin-scoped session. */
function AccountMenu() {
  const [open, setOpen] = useState(false);
  const s = session.value;
  if (!s) {
    return (
      <div class="auth-actions">
        <button class="link-btn login" onClick={() => gotoAuth("login")}>Log In</button>
        <button class="primary small" onClick={() => gotoAuth("sign-up")}>Start Free</button>
      </div>
    );
  }
  return (
    <div class="account-menu">
      <button class="secondary small account-chip" onClick={() => setOpen(!open)} title={s.email}>{s.email}</button>
      {open && (
        <div class="account-dropdown">
          <div class="account-dropdown-hint">Signed in as<br /><b>{s.email}</b></div>
          <div class="account-dropdown-divider" />
          <button class="account-dropdown-item" onClick={() => { signOut(); setOpen(false); }}>Sign out</button>
        </div>
      )}
    </div>
  );
}

/** Cross-product switcher. Kept markup- and style-identical to the backtest and
 *  portfolio apps' ProductMenu (no shared component package yet). Cross-product
 *  links are full-page navigations; the origin-scoped SSO session carries over. */
function ProductMenu() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);
  return (
    <div class="product-menu" onClick={(e) => e.stopPropagation()}>
      <button class="product-menu-toggle" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>Context</span>
        <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4.5l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
      </button>
      {open && (
        <div class="product-menu-dropdown" role="menu">
          <a class="product-menu-item" href="/backtest/" role="menuitem">Strategies</a>
          <a class="product-menu-item" href="/portfolio/" role="menuitem">Portfolios</a>
          <a class="product-menu-item active" href="/context/" role="menuitem">Context</a>
          <a class="product-menu-item" href="/portfolio/library" role="menuitem">Library</a>
          <div class="product-menu-sep" />
          <a class="product-menu-item" href="/docs/" role="menuitem">Docs</a>
        </div>
      )}
    </div>
  );
}

function TrendBadge({ trend }: { trend: TrendRead["trend"] }) {
  if (trend === null) return <span class="badge badge-muted">No data</span>;
  return <span class={"badge " + (trend === "bullish" ? "badge-bull" : "badge-bear")}>{trend === "bullish" ? "Bullish" : "Bearish"}</span>;
}

function DotRamp({ filled, bullish }: { filled: number; bullish: boolean }) {
  return (
    <span class="dot-ramp" title="Trend strength (distance from the average)">
      {[0, 1, 2, 3].map((i) => (
        <span class={"dot" + (i < filled ? (bullish ? " on-bull" : " on-bear") : "")} />
      ))}
    </span>
  );
}

function AssetRow({ ctx, read, metric }: { ctx: AssetContext; read: TrendRead; metric: "slope" | "sharpe" }) {
  const bullish = read.trend === "bullish";
  return (
    <div class="asset-row">
      <div class="ar-lead">
        <Sparkline data={read.spark} ma={read.ma} bullish={bullish} />
        <div class="ar-name">
          <div class="ar-label-row">
            <span class="ar-label">{ctx.def.label}</span>
            <span class={"ar-cat cat-" + ctx.def.category.replace(/\s+/g, "-").toLowerCase()}>{ctx.def.category}</span>
          </div>
          <div class="ar-blurb">
            <span class="mono ar-sym">{ctx.def.symbol}</span> · {ctx.def.blurb}
          </div>
        </div>
      </div>
      <div class="ar-metrics">
        <div class="ar-num">
          <div class="ar-level mono">{formatLevel(ctx)}</div>
          <div class={"ar-dist mono " + (read.distance >= 0 ? "pos" : "neg")}>{signedPct(read.distance)} vs avg</div>
        </div>
        <div class="ar-sharpe mono" title={metric === "slope" ? "Trend of the average, annualized (% per year)" : "Annualized Sharpe over the window"}>
          {metric === "slope" ? (
            <>
              <span class="ar-sharpe-v">{signedPct(read.slope)}</span>
              <span class="ar-sharpe-k">Trend</span>
            </>
          ) : (
            <>
              <span class="ar-sharpe-v">{signedSharpe(read.sharpe)}</span>
              <span class="ar-sharpe-k">Sharpe</span>
            </>
          )}
        </div>
        <div class="ar-age mono" title="Weeks since price last crossed the average (how long this trend has persisted)">
          <span class="ar-sharpe-v">{read.ageWeeks == null ? "—" : `${read.ageWeeks} ${read.ageWeeks === 1 ? "week" : "weeks"}`}</span>
          <span class="ar-sharpe-k">Trend age</span>
        </div>
        <DotRamp filled={strengthDots(read.distance)} bullish={bullish} />
        <TrendBadge trend={read.trend} />
      </div>
    </div>
  );
}

type View = "seasonal" | "generational";
type SortMode = "slope" | "sharpe" | "type";
type CatFilter = "all" | AssetCategory;

const CATEGORY_ORDER: AssetCategory[] = ["Equities", "Crypto", "Currency", "Commodity", "Bonds", "Interest Rates"];

function okResults(): AssetContext[] {
  return results.value.filter((r): r is AssetContext => !isError(r));
}

function readOf(c: AssetContext, view: View): TrendRead {
  return view === "seasonal" ? c.seasonal : c.generational;
}

/** Loaded assets ordered for display. Default ("slope"): the average's slope,
 *  steepest-rising first. "sharpe": the window's Sharpe. "type": grouped by
 *  asset class (CATEGORY_ORDER), slope-ordered within each class. Sort is stable,
 *  so the class pass keeps the metric order inside each group. */
function ranked(view: View, sortMode: SortMode): AssetContext[] {
  const metricOf = (c: AssetContext) => (sortMode === "sharpe" ? readOf(c, view).sharpe : readOf(c, view).slope);
  const arr = [...okResults()].sort((a, b) => metricOf(b) - metricOf(a));
  if (sortMode === "type") {
    arr.sort((a, b) => CATEGORY_ORDER.indexOf(a.def.category) - CATEGORY_ORDER.indexOf(b.def.category));
  }
  return arr;
}

/** Bullish breadth over ONE asset class (or all loaded assets). Breadth is only
 *  meaningful within a homogeneous class — a mixed-asset count conflates signals
 *  that mean different things — so the UI shows it per class, never board-wide. */
function breadth(view: View, cat: CatFilter): { bull: number; total: number } {
  const scoped = okResults().filter((c) => cat === "all" || c.def.category === cat);
  const rated = scoped.filter((c) => readOf(c, view).trend !== null);
  return { bull: rated.filter((c) => readOf(c, view).trend === "bullish").length, total: rated.length };
}

function conditionWord(bull: number, total: number): string {
  if (total === 0) return "—";
  const r = bull / total;
  if (r >= 0.66) return "Broadly Bullish";
  if (r > 0.34) return "Mixed";
  return "Broadly Bearish";
}

function Section({ view, cat, sortMode, onCat, onSort }: {
  view: View;
  cat: CatFilter;
  sortMode: SortMode;
  onCat: (c: CatFilter) => void;
  onSort: (s: SortMode) => void;
}) {
  const seasonal = view === "seasonal";
  const ok = okResults();
  const cats = CATEGORY_ORDER.filter((c) => ok.some((a) => a.def.category === c));
  // If the active class has no loaded assets (e.g. after a reload dropped one), fall back to Overall.
  const activeCat: CatFilter = cat === "all" || cats.includes(cat) ? cat : "all";
  const rows = ranked(view, sortMode).filter((c) => activeCat === "all" || c.def.category === activeCat);
  const errors = results.value.filter(isError);
  const b = breadth(view, activeCat);
  // The metric shown on each row follows the ranking metric (slope, unless Sharpe).
  const metric: "slope" | "sharpe" = sortMode === "sharpe" ? "sharpe" : "slope";

  return (
    <section class="ctx-card">
      <div class={"ctx-card-head " + (seasonal ? "band-seasonal" : "band-generational")}>
        <div>
          <div class="eyebrow">{seasonal ? "Seasonal · 1 Year trend" : "Generational · 4 Year trend"}</div>
          <h2>{seasonal ? "Seasonal Trends" : "Generational Trends"}</h2>
          <p class="ctx-card-sub">
            {seasonal
              ? "Where each asset trades relative to its 1 Year average — a read on the past year's regime."
              : "Where each asset trades relative to its 4 Year average — the multi-year, structural trend."}
          </p>
        </div>
      </div>

      <div class="cat-tabbar">
        <div class="cat-tabs" role="tablist" aria-label="Asset class">
          <button role="tab" aria-selected={activeCat === "all"} class={"cat-tab" + (activeCat === "all" ? " active" : "")} onClick={() => onCat("all")}>Overall</button>
          {cats.map((c) => (
            <button key={c} role="tab" aria-selected={activeCat === c} class={"cat-tab" + (activeCat === c ? " active" : "")} onClick={() => onCat(c)}>{c}</button>
          ))}
        </div>

        {activeCat === "all" ? (
          <div class="sort-toggle">
            <span class="sort-label">Sort</span>
            <button class={sortMode === "slope" ? "active" : ""} onClick={() => onSort("slope")} title="Rank by the trend of the average">Trend</button>
            <button class={sortMode === "sharpe" ? "active" : ""} onClick={() => onSort("sharpe")} title="Rank by risk-adjusted momentum">Sharpe</button>
            <button class={sortMode === "type" ? "active" : ""} onClick={() => onSort("type")} title="Group by asset class">Asset type</button>
          </div>
        ) : (
          <div class="cat-breadth" title="How many assets in this class are bullish">
            <span class="cat-breadth-word">{conditionWord(b.bull, b.total)}</span>
            <div class="breadth-bar" aria-hidden="true">
              <div class="breadth-fill" style={{ width: b.total ? `${(b.bull / b.total) * 100}%` : "0%" }} />
            </div>
            <span class="cat-breadth-count mono">{b.bull}/{b.total} bullish</span>
          </div>
        )}
      </div>

      <div class="asset-list">
        {rows.map((c) => (
          <AssetRow key={c.def.key} ctx={c} read={readOf(c, view)} metric={metric} />
        ))}
      </div>
      {errors.length > 0 && (
        <div class="ctx-card-foot">
          Couldn't load: {errors.map((e) => e.def.label).join(", ")}. Data may be delayed — reload to retry.
        </div>
      )}
    </section>
  );
}

export function App() {
  const view = useSignal<View>("seasonal");
  const cat = useSignal<CatFilter>("all");
  const sortMode = useSignal<SortMode>("slope");
  useEffect(() => { void loadAll(); void refreshSession(); }, []);

  const asOf = (() => {
    const ok = results.value.filter((r): r is AssetContext => !isError(r));
    if (!ok.length) return null;
    return Math.max(...ok.map((c) => c.asOf));
  })();

  return (
    <div class="ctx-app">
      <header class="topbar">
        <div class="brand">
          <a href="/" class="brand-mark">MarketHeist</a>
          <ProductMenu />
        </div>
        <div class="topbar-right">
          {loadedAt.value && <span class="updated"><span class="pulse-dot" /> Updated {formatClock(loadedAt.value)}</span>}
          <SocialLinks />
          <AccountMenu />
        </div>
      </header>

      <main class="ctx-main">
        <div class="ctx-hero">
          <div class="eyebrow">Market Context · the environment your portfolio lives in</div>
          <h1>Which way is the market leaning?</h1>
          <p class="ctx-lede">
            The trend regime for fourteen major markets — stocks, crypto, gold, the dollar, crude, bonds and Treasury yields — at two
            horizons. A <strong>seasonal</strong> read (price vs its 1 Year average) and a <strong>generational</strong> one
            (price vs its 4 Year average).
            {asOf && <span class="mono ctx-asof"> Prices as of {formatAsOf(asOf)}.</span>}
          </p>
        </div>

        <div class="view-toggle" role="tablist" aria-label="Trend horizon">
          <button role="tab" aria-selected={view.value === "seasonal"} class={view.value === "seasonal" ? "active" : ""} onClick={() => (view.value = "seasonal")}>
            Seasonal <span class="vt-sub">1 Year</span>
          </button>
          <button role="tab" aria-selected={view.value === "generational"} class={view.value === "generational" ? "active" : ""} onClick={() => (view.value = "generational")}>
            Generational <span class="vt-sub">4 Year</span>
          </button>
        </div>

        {loading.value && results.value.length === 0 ? (
          <div class="ctx-loading">
            <div class="spinner" />
            <p>Reading the tape — fetching weekly history for fourteen markets…</p>
          </div>
        ) : (
          <Section
            view={view.value}
            cat={cat.value}
            sortMode={sortMode.value}
            onCat={(c) => (cat.value = c)}
            onSort={(s) => (sortMode.value = s)}
          />
        )}

        <section class="method">
          <div class="eyebrow">How to read this</div>
          <div class="method-grid">
            <div>
              <h3>Seasonal — 1 Year</h3>
              <p>Price above its 1 Year average is a <b>bullish</b> trend; below is <b>bearish</b>. This captures where an asset sits within the past year.</p>
            </div>
            <div>
              <h3>Generational — 4 Year</h3>
              <p>Price above its 4 Year average marks a <b>bullish</b> structural, multi-cycle trend; below is <b>bearish</b>.</p>
            </div>
            <div>
              <h3>The ranking</h3>
              <p>By default rows are ordered by the <b>trend</b> of the average — how fast the 1 Year (or 4 Year) average is rising or falling. Switch <b>Sort</b> to the <b>Sharpe ratio</b> (risk-adjusted momentum) or group by asset type.</p>
            </div>
            <div>
              <h3>
                The strength dots
                <span class="method-dots"><span class="dot on-bull" /><span class="dot on-bull" /><span class="dot on-bull" /><span class="dot on-bull" /></span>
              </h3>
              <p>How far price sits from its average — <b>1 dot</b> (within 2%) to <b>4 dots</b> (15%+). <b class="pos-word">Green</b> = above its average (bullish), <b class="neg-word">red</b> = below (bearish). A gauge of trend strength, not a buy/sell signal.</p>
            </div>
            <div>
              <h3>Yields, not prices</h3>
              <p>For the 10- and 30-year rows the level is the <b>yield</b>, so "bullish" means yields are rising above their average — read alongside the bond (TLT) row.</p>
            </div>
          </div>
        </section>

        <footer class="ctx-foot">
          <span class="mono">MarketHeist Context</span> · market environment, updated from weekly data ·{" "}
          <a href="/portfolio/">Portfolios</a> · <a href="/backtest/">Backtest</a> · <a href="/docs/">Docs</a>
          <div class="disclaimer">Educational information about market trends. Not investment advice.</div>
        </footer>
      </main>
    </div>
  );
}
